import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import ScopeToggle from "../../components/ScopeToggle";
import HeatMapGrid from "../../components/HeatMapGrid";
import { downloadBlob, exportSvgAsPng } from "../../lib/download";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { FileSpreadsheet, Presentation, ImageDown, Loader2 } from "lucide-react";

const inr = (n) => (n == null ? "—" : `₹${Math.round(Number(n)).toLocaleString("en-IN")}`);

function Tile({ testid, label, value, color, delta, basis }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid={testid}>
      <div className="text-3xl font-semibold tabular-nums" style={{ color }}>{value ?? "—"}</div>
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mt-1">{label}</div>
      {delta != null && (
        <div className="text-[10px] text-[#6B7280] mt-1 tabular-nums">
          {delta > 0 ? "+" : ""}{delta} MoM{basis ? ` · ${basis}` : ""}
        </div>
      )}
    </div>
  );
}

function Card({ title, children, actions, testid }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-4 sm:p-5" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="font-serif text-base text-[#0E2240]">{title}</h2>
        {actions}
      </div>
      {children}
    </div>
  );
}

const TH = "text-left text-[10px] uppercase tracking-wider text-[#6B7280] px-2 py-1.5 border-b border-gray-200";
const TD = "px-2 py-1.5 text-xs text-[#0E2240] border-b border-gray-100";

export default function MdDashboard() {
  const { user } = useAuth();
  const [mode, setMode] = useState("Live");
  const [hl, setHl] = useState(null);
  const [hm, setHm] = useState(null);
  const [cost, setCost] = useState(null);
  const [unc, setUnc] = useState(null);
  const [cv, setCv] = useState(null);
  const [att, setAtt] = useState(null);
  const [ttf, setTtf] = useState(null);
  const [ttfFilter, setTtfFilter] = useState({ months: 12, vertical: "", zone_id: "" });
  const [drill, setDrill] = useState(null);
  const [busy, setBusy] = useState(null);
  const ttfRef = useRef(null);

  useEffect(() => {
    setHl(null); setHm(null); setCost(null); setUnc(null);
    api.get("/analytics/headline", { params: { mode } }).then(({ data }) => setHl(data)).catch(() => {});
    api.get("/analytics/heatmap", { params: { mode } }).then(({ data }) => setHm(data)).catch(() => {});
    api.get("/analytics/cost", { params: { mode } }).then(({ data }) => setCost(data)).catch(() => {});
    api.get("/analytics/uncovered", { params: { mode } }).then(({ data }) => setUnc(data)).catch(() => {});
  }, [mode]);

  useEffect(() => {
    api.get("/analytics/critical-vacancies", { params: { days: 45 } }).then(({ data }) => setCv(data)).catch(() => {});
    api.get("/analytics/attrition").then(({ data }) => setAtt(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const p = { months: ttfFilter.months };
    if (ttfFilter.vertical) p.vertical = ttfFilter.vertical;
    if (ttfFilter.zone_id) p.zone_id = ttfFilter.zone_id;
    api.get("/analytics/ttf-attribution", { params: p }).then(({ data }) => setTtf(data)).catch(() => {});
  }, [ttfFilter]);

  const openDrill = useCallback(async (b, d) => {
    setDrill({ loading: true, branch: b.branch_name, dept: d.dept_name });
    try {
      const { data } = await api.get("/analytics/heatmap/drill", { params: { branch_id: b.branch_id, dept_id: d.dept_id } });
      setDrill({ ...data, loading: false });
    } catch (e) {
      toast.error(apiError(e));
      setDrill(null);
    }
  }, []);

  const doExport = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "excel") await downloadBlob("/analytics/export/excel", `hire360-manning-${mode.toLowerCase()}.xlsx`, { mode });
      else await downloadBlob("/analytics/export/pptx", `hire360-board-pack-${mode.toLowerCase()}.pptx`, { mode });
      toast.success(kind === "excel" ? "Excel exported" : "Board pack exported");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const fillPct = hl?.fill_percent != null ? `${(hl.fill_percent * 100).toFixed(1)}%` : "—";
  const btn = "flex items-center gap-1.5 border border-gray-300 rounded-sm px-3 py-1.5 text-xs font-medium text-[#0E2240] bg-white hover:border-[#0E2240] transition-colors duration-200 disabled:opacity-50";

  return (
    <div data-testid="md-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">Command Centre</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#0E2240] tracking-tight mt-1">
            Welcome, {user?.full_name?.split(" ")[0]}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ScopeToggle value={mode} onChange={setMode} />
          <button className={btn} data-testid="export-excel-btn" disabled={busy === "excel"} onClick={() => doExport("excel")}>
            {busy === "excel" ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} className="text-[#1B7F3B]" />} Excel
          </button>
          <button className={btn} data-testid="export-pptx-btn" disabled={busy === "pptx"} onClick={() => doExport("pptx")}>
            {busy === "pptx" ? <Loader2 size={14} className="animate-spin" /> : <Presentation size={14} className="text-[#BB162B]" />} Board Pack
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        <Tile testid="hl-sanctioned" label="Sanctioned" value={hl?.sanctioned} color="#0E2240" delta={hl?.mom?.sanctioned} basis={hl?.mom?.basis} />
        <Tile testid="hl-active" label="Active" value={hl?.active} color="#1B7F3B" delta={hl?.mom?.active} />
        <Tile testid="hl-shortfall" label="Shortfall" value={hl?.shortfall} color="#BB162B" delta={hl?.mom?.shortfall} />
        <Tile testid="hl-surplus" label="Surplus" value={hl?.surplus} color="#1E5AA8" delta={hl?.mom?.surplus} />
        <Tile testid="hl-fill" label="Fill %" value={fillPct} color="#BF9B30" />
      </div>

      <Card title="Network Manning Heat Map" testid="heatmap-card">
        <HeatMapGrid data={hm} onDrill={openDrill} />
      </Card>

      <div className="mt-6">
        <Card
          title="Time-to-Fill Attribution — who holds the clock"
          testid="ttf-card"
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <select data-testid="ttf-months-select" value={ttfFilter.months}
                onChange={(e) => setTtfFilter((f) => ({ ...f, months: Number(e.target.value) }))}
                className="border border-gray-300 rounded-sm px-2 py-1 text-xs bg-white">
                <option value={6}>6 months</option>
                <option value={12}>12 months</option>
              </select>
              <select data-testid="ttf-vertical-select" value={ttfFilter.vertical}
                onChange={(e) => setTtfFilter((f) => ({ ...f, vertical: e.target.value }))}
                className="border border-gray-300 rounded-sm px-2 py-1 text-xs bg-white">
                <option value="">All verticals</option>
                {(ttf?.verticals || []).map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
              <select data-testid="ttf-zone-select" value={ttfFilter.zone_id}
                onChange={(e) => setTtfFilter((f) => ({ ...f, zone_id: e.target.value }))}
                className="border border-gray-300 rounded-sm px-2 py-1 text-xs bg-white">
                <option value="">All zones</option>
                {(ttf?.zones || []).map((z) => <option key={z.zone_id} value={z.zone_id}>{z.zone_name}</option>)}
              </select>
              <button className={btn} data-testid="export-ttf-png-btn"
                onClick={() => exportSvgAsPng(ttfRef.current, "hire360-ttf-attribution.png")}>
                <ImageDown size={14} className="text-[#1E5AA8]" /> PNG
              </button>
            </div>
          }
        >
          <div ref={ttfRef} className="h-72">
            {ttf && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ttf.months} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#6B7280" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#6B7280" }} />
                  <Tooltip formatter={(v, n) => [`${v} days`, n]} contentStyle={{ fontSize: 12, borderRadius: 2 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="business" stackId="a" fill="#0E2240" name="Business" />
                  <Bar dataKey="hr" stackId="a" fill="#BF9B30" name="HR" />
                  <Bar dataKey="candidate" stackId="a" fill="#1E5AA8" name="Candidate" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          {ttf?.summary && (
            <div className="text-[11px] text-[#6B7280] mt-2 tabular-nums" data-testid="ttf-summary">
              Avg per requisition: Business {ttf.summary.business}d · HR {ttf.summary.hr}d · Candidate {ttf.summary.candidate}d · {ttf.summary.count} requisitions
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Card title="Critical Vacancies — revenue roles open 45+ days" testid="critical-vacancies-card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr>
                <th className={TH}>MRF</th><th className={TH}>Branch</th><th className={TH}>Role</th>
                <th className={TH}>Days</th><th className={TH}>Pending With</th><th className={TH}>Cost of Vacancy</th>
              </tr></thead>
              <tbody>
                {(cv || []).slice(0, 10).map((r) => (
                  <tr key={r.req_id}>
                    <td className={TD}><Link to={`/requisitions/${r.req_id}`} className="text-[#1E5AA8] hover:underline">{r.req_no}</Link></td>
                    <td className={TD}>{r.branch_name}</td>
                    <td className={TD}>{r.desig_name}</td>
                    <td className={`${TD} tabular-nums font-semibold text-[#BB162B]`}>{r.days_open}</td>
                    <td className={TD}>{r.pending_with || "—"}</td>
                    <td className={`${TD} tabular-nums`}>{inr(r.cost_of_vacancy)}</td>
                  </tr>
                ))}
                {cv && cv.length === 0 && <tr><td colSpan={6} className="px-2 py-6 text-center text-xs text-[#6B7280]">No revenue-facing vacancy open beyond 45 days</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="Cost of manning imbalance" testid="cost-card">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="border border-gray-200 rounded-sm p-3">
              <div className="text-xl font-semibold text-[#BB162B] tabular-nums" data-testid="cov-total">{inr(cost?.cost_of_vacancy_total)}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mt-1">Cost of vacancy (revenue roles)</div>
            </div>
            <div className="border border-gray-200 rounded-sm p-3">
              <div className="text-xl font-semibold text-[#1E5AA8] tabular-nums" data-testid="cos-total">{inr(cost?.cost_of_surplus_monthly)}<span className="text-xs font-normal">/mo</span></div>
              <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mt-1">Cost of surplus manpower</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr><th className={TH}>Branch</th><th className={TH}>Dept</th><th className={TH}>Role</th><th className={TH}>Surplus</th><th className={TH}>Monthly Cost</th></tr></thead>
              <tbody>
                {(cost?.cos_rows || []).slice(0, 6).map((r, i) => (
                  <tr key={i}>
                    <td className={TD}>{r.branch_name}</td><td className={TD}>{r.dept_name}</td><td className={TD}>{r.desig_name}</td>
                    <td className={`${TD} tabular-nums`}>{r.surplus}</td><td className={`${TD} tabular-nums`}>{inr(r.monthly_cost)}</td>
                  </tr>
                ))}
                {cost && cost.cos_rows.length === 0 && <tr><td colSpan={5} className="px-2 py-6 text-center text-xs text-[#6B7280]">No over-manned cells</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6 mb-8">
        <Card title="Attrition ticker" testid="attrition-card">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="border border-gray-200 rounded-sm p-3">
              <div className="text-2xl font-semibold text-[#0E2240] tabular-nums" data-testid="exits-mtd">{att?.exits_mtd ?? "—"}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mt-1">Exits this month</div>
            </div>
            <div className="border border-gray-200 rounded-sm p-3">
              <div className="text-2xl font-semibold text-[#BB162B] tabular-nums" data-testid="early-attrition">{att?.early_attrition_90d ?? "—"}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mt-1">Early attrition (≤90 days)</div>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-2">Top exit reasons — last 90 days</div>
          {(att?.top_reasons || []).map((r) => (
            <div key={r.reason} className="flex items-center justify-between text-xs py-1 border-b border-gray-100 last:border-0">
              <span className="text-[#0E2240]">{r.reason}</span>
              <span className="tabular-nums text-[#6B7280]">{r.count}</span>
            </div>
          ))}
          {att && att.top_reasons.length === 0 && <div className="text-xs text-[#6B7280] py-3">No exits in the last 90 days</div>}
        </Card>

        <Card title="Uncovered shortfall — no requisition raised" testid="uncovered-card">
          {(unc || []).slice(0, 6).map((b) => (
            <div key={b.branch_name} className="border border-[#BB162B]/30 rounded-sm p-3 mb-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#0E2240]">{b.branch_name} <span className="text-[#6B7280] font-normal">· {b.zone_name}</span></span>
                <span className="text-xs font-semibold text-[#BB162B] tabular-nums">{b.total_short} uncovered</span>
              </div>
              <div className="text-[11px] text-[#6B7280] mt-1">
                {b.cells.slice(0, 3).map((c) => `${c.dept_name} · ${c.desig_name} (${c.short})`).join(", ")}
                {b.cells.length > 3 && ` +${b.cells.length - 3} more`}
              </div>
            </div>
          ))}
          {unc && unc.length === 0 && <div className="text-xs text-[#6B7280] py-3">All shortfall positions have open requisitions — no blind spots</div>}
        </Card>
      </div>

      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="drill-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#0E2240]">
              {drill?.branch_name || drill?.branch} · {drill?.dept_name || drill?.dept}
            </DialogTitle>
          </DialogHeader>
          {drill?.loading ? (
            <div className="py-8 text-center text-sm text-[#6B7280]">Loading…</div>
          ) : drill && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-2">Designation-wise position</div>
              <table className="w-full mb-5">
                <thead><tr><th className={TH}>Designation</th><th className={TH}>Grade</th><th className={TH}>Sanctioned</th><th className={TH}>Active</th><th className={TH}>Gap</th><th className={TH}>Open Reqs</th></tr></thead>
                <tbody>
                  {(drill.designations || []).map((r) => (
                    <tr key={r.desig_id}>
                      <td className={TD}>{r.desig_name}</td><td className={TD}>{r.grade}</td>
                      <td className={`${TD} tabular-nums`}>{r.sanctioned}</td><td className={`${TD} tabular-nums`}>{r.active}</td>
                      <td className={`${TD} tabular-nums font-semibold ${r.gap > 0 ? "text-[#BB162B]" : r.gap < 0 ? "text-[#1E5AA8]" : ""}`}>{r.gap}</td>
                      <td className={`${TD} tabular-nums`}>{r.open_reqs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-2">Open requisitions — where each is stuck</div>
              {(drill.open_requisitions || []).length === 0 && <div className="text-xs text-[#6B7280] py-2">No open requisitions for this cell</div>}
              {(drill.open_requisitions || []).map((r) => (
                <Link key={r.req_id} to={`/requisitions/${r.req_id}`}
                  className="flex flex-wrap items-center justify-between gap-2 border border-gray-200 rounded-sm px-3 py-2 mb-1.5 hover:border-[#0E2240] transition-colors duration-200">
                  <div>
                    <span className="text-xs font-medium text-[#1E5AA8]">{r.req_no}</span>
                    <span className="text-xs text-[#0E2240] ml-2">{r.desig_name}</span>
                  </div>
                  <div className="text-[11px] text-[#6B7280] tabular-nums">
                    {r.status}{r.stage ? ` · ${r.stage}` : ""}{r.pending_with ? ` · with ${r.pending_with}` : ""} · {r.days_open}d open
                  </div>
                </Link>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
